import { forwardRef, type HTMLProps, type LegacyRef, useEffect, useState } from 'react';
import type { DateRange } from 'rsuite/esm/DateRangePicker';
import type {
  ApiProject,
  ApiReturnProject,
} from '@highimpact-frontend/api-types';
import assert from 'assert';
import { useAppSelector } from '../../store/hooks';
import StateDecorator from '../utils/state/StateDecorator';
import 'react-pure-modal/dist/react-pure-modal.min.css';
import ProjectService from '../../api/services/config/project/ProjectService';
import { isDataState } from '../../store/data/state';
import { recordNotEqual } from '../../utils/functions';
import { createValidator, useForm } from '../../utils/hooks/useForm';
import { resetExternalState } from '../../store/data/slice';
import { useAppDispatch } from '../../store/hooks';
import { useSubmit } from '../../utils/hooks/useSubmit';
import CustomizedDialog from '../utils/MUISpecifics/dialog/CustumizedDialog';
import { Button, Stack } from '@mui/material';
import TextInput from '../utils/MUISpecifics/inputText/TextInput';
import DateTimeRangePickerComponent from '../utils/MUISpecifics/DateTimeRangePicker';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import DropdownItemsSelector from '../utils/MUISpecifics/dropDownItemSelector/DropDownItemSelector';
import UserProjectService from '../../api/services/config/user_project/UserProjectService';
import { isAxiosError } from '../../api/utils';
import DropDownMultiAutocompleteComponent from '../utils/MUISpecifics/dropDownMultiSelector/dropDownAutocompleteMultiSelector';

// Component props.
export interface FormProps extends HTMLProps<HTMLInputElement> {
  handleClose: (value: boolean) => void;
  project: ApiReturnProject | null;
  isModalOpen: boolean;
}

// Component props.
export interface FormPropsNotNull extends FormProps {
  project: ApiReturnProject;
}

function projectNotNull(
  props: FormProps | FormPropsNotNull
): props is FormPropsNotNull {
  return Boolean(props.project);
}

function EditProjectFormOrNothing(props: FormProps) {
  if (!projectNotNull(props)) return <></>;
  else {
    return <EditProjectForm {...props} />;
  }
}

// Component.
function EditProjectForm(
  { isModalOpen, project, children, ...props }: FormPropsNotNull,
  ref: LegacyRef<HTMLDivElement> | undefined
) {
  // use translation here
  const { t } = useTranslation('components.projects');

  const dispatch = useAppDispatch();
  const users = useAppSelector((state) => state.data_slice.externals.users);
  const { submit, state } = useSubmit();
  const [projectAdministratorId, setProjectAdministratorId] = useState<
    number | null
  >(null);
  const [selectedUsers, setSelectedUsers] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [initialUserProjects, setInitialUserProjects] = useState<
    Array<{ user_id: number; project_id: number; is_admin: boolean }>
  >([]);

  const defaultValues: ApiProject = {
    name: project.name,
    start_date: project.start_date ?? null,
    end_date: project.end_date ?? null,
    is_admin: project.is_admin ?? false,
  };

  useEffect(() => {
    if (!isDataState(users)) return;
    let isCancelled = false;
    const loadUserProjects = async () => {
      const userProjects =
        await UserProjectService.getUsersProjectsByUserIdOrByProjectId(
          undefined,
          project.id
        );
      if (isCancelled) return;
      const administratorId =
        userProjects.find((userProject) => userProject.is_admin)?.user_id ??
        null;
      const associatedUsers = userProjects
        .filter((userProject) => !userProject.is_admin)
        .map((userProject) => users.data[userProject.user_id])
        .filter((user) => user !== undefined)
        .map((user) => ({ id: user.id, name: user.name + " " + user.surname }));
      setProjectAdministratorId(administratorId);
      setSelectedUsers(associatedUsers);
      setInitialUserProjects(userProjects);
    };
    void loadUserProjects();
    return () => {
      isCancelled = true;
    };
  }, [project.id, users]);

  // Update the start and end dates.
  const handleDateUpdate = (interval: DateRange | null) => {
    if (!interval) {
      methods.setMultipleValues({
        start_date: null,
        end_date: null,
      });
      return;
    }
    const [start, end] = interval;
    start.setTime(start.getTime() - start.getTimezoneOffset() * 60 * 1000);
    end.setTime(end.getTime() - end.getTimezoneOffset() * 60 * 1000);
    methods.setMultipleValues({
      start_date: start.toISOString(),
      end_date: end.toISOString(),
    });
  };

  // Ckeck for changes.

  // Handle form submission.
  const onSubmit = async (data: ApiProject) => {
    submit({
      callbacks: [
        async () => {
          await ProjectService.update(project.id, data);
          const associatedUserIds = new Set([
            ...selectedUsers.map((user) => user.id),
            ...(projectAdministratorId === null ? [] : [projectAdministratorId]),
          ]);
          await Promise.all(
            initialUserProjects
              .filter((userProject) => !associatedUserIds.has(userProject.user_id))
              .map((userProject) => UserProjectService.delete(userProject))
          );
          await Promise.all(
            [...associatedUserIds].map(async (userId) => {
              const userProject = {
                user_id: userId,
                project_id: project.id,
                is_admin: userId === projectAdministratorId,
              };
              try {
                await UserProjectService.update(userProject);
              } catch (error) {
                if (!isAxiosError(error) || error.response?.status !== 404) {
                  throw error;
                }
                await UserProjectService.add(userProject);
              }
            })
          );
        },
        () => methods.reset(),
        () => props.handleClose(isModalOpen),
        () => dispatch(resetExternalState({ external_key: 'projects' })),
        () => dispatch(resetExternalState({ external_key: 'project_sites' })),
      ],
      errorMessages: {
        anyError: t(
          'Une erreur est survenue lors de la modification du projet'
        ),
        unicityError: t('Un projet avec le même nom existe déjà'),
      },
      successMessage: t('Le projet a été modifié avec succès'),
    });
  };

  /**
   * Create validation rule for each field.
   */
  const validator = createValidator<ApiProject>({
    name: [
      (n) => !n || n.trim().length <= 3
        ? t('Entrer au moins 4 caractères')
        : undefined,
    ],
    start_date: [],
    end_date: [],
    is_admin: []
  });

  // Define form hook.
  const methods = useForm<ApiProject>(defaultValues, validator, onSubmit);

  // Update the name input.
  const handleName = (value: string) => {
    methods.setValue('name', value);
  };

  const initialAdministratorId =
    initialUserProjects.find((userProject) => userProject.is_admin)?.user_id ??
    null;
  const initialSelectedUserIds = initialUserProjects
    .filter((userProject) => !userProject.is_admin)
    .map((userProject) => userProject.user_id)
    .sort();
  const selectedUserIds = selectedUsers
    .map((user) => user.id)
    .sort();
  const haveUserProjectsChanged =
    projectAdministratorId !== initialAdministratorId ||
    selectedUserIds.length !== initialSelectedUserIds.length ||
    selectedUserIds.some(
      (userId, index) => userId !== initialSelectedUserIds[index]
    );

  return StateDecorator([users], ref!, () => {
    assert(isDataState(users));

    return (
      <CustomizedDialog
        title={t('Modifier le projet')}
        onCancel={() => {
          props.handleClose(true);
          methods.reset();
        }}
        open={isModalOpen}
      >
        <Stack spacing={2}>
          <form
            onSubmit={methods.onSubmit}
            className={`form ${props.className || ''}`}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <TextInput
              onChange={handleName}
              label={t('Nom du projet')}
              name={'name'}
              placeholder={t('Entrer le nom du projet')}
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('name') }
                  : { tag: 'Not submitted' }
              }
              defaultValue={methods.watch('name') ?? ''}
              required
            />

            <DateTimeRangePickerComponent
              label={t('Période du projet')}
              required={false}
              format="DD/MM/YYYY"
              onChange={handleDateUpdate}
              error={
                methods.getErrors('start_date').length > 0 ||
                methods.getErrors('end_date').length > 0
              }
              helperText={
                methods.getErrors('start_date')[0] ||
                methods.getErrors('end_date')[0] ||
                ''
              }
              defaultValues={[
                methods.watch('start_date')
                  ? new Date(methods.watch('start_date') as string)
                  : null,
                methods.watch('end_date')
                  ? new Date(methods.watch('end_date') as string)
                  : null,
              ]}
              safeDefault={false}
            />
            <DropdownItemsSelector
              key={`${project.id}-${projectAdministratorId ?? 'none'}`}
              defaultValue={projectAdministratorId}
              onSelect={(selectedId) => {
                setProjectAdministratorId(selectedId);
                setSelectedUsers((currentUsers) =>
                  currentUsers.filter((user) => user.id !== selectedId)
                );
              }}
              label={t('Administrateur du projet')}
              values={Object.values(users.data).map(user => ({ id: user.id, name: user.name + " " + user.surname }))}
              disabled={false}
              name="project_administrator_id"
              required={false}
            />
            <DropDownMultiAutocompleteComponent
              label={t('Utilisateurs associés au projet')}
              options={Object.values(users.data)
                .filter((user) => user.id !== projectAdministratorId)
                .map((user) => ({ id: user.id, name: user.name + " " + user.surname }))}
              value={selectedUsers}
              onChange={setSelectedUsers}
            />
            <Button
              type="submit"
              className="form-button-submit"
              variant="outlined"
              loadingPosition="end"
              data-testid="submit-edited-project-button"
              endIcon={<SaveIcon />}
              disabled={
                !recordNotEqual(defaultValues, methods.watchAll()) &&
                !haveUserProjectsChanged
              }
            >
              {state === 'Loading' ? t('Envoi...') : t('Modifier')}
            </Button>
          </form>
        </Stack>
      </CustomizedDialog>
    );
  });
}

export default forwardRef(EditProjectFormOrNothing);
